import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import * as workflowAPI from "./workflowAPI"
import { normalizeError } from "../../utils/errorHelpers"

export const loadConditions = createAsyncThunk(
  "workflow/loadConditions",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchAllConditions()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadTriggers = createAsyncThunk(
  "workflow/loadTriggers",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchAllTriggers()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadActions = createAsyncThunk(
  "workflow/loadActions",
  async (filters = {}, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchAllActions(filters)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadActionTypes = createAsyncThunk(
  "workflow/loadActionTypes",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchActionTypes()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const updateConfig = createAsyncThunk(
  "workflow/updateConfig",
  async ({ id, config }, { rejectWithValue }) => {
    try {
      return await workflowAPI.updateActionConfig(id, config)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const toggleActionStatus = createAsyncThunk(
  "workflow/toggleActionStatus",
  async (id, { rejectWithValue }) => {
    try {
      return await workflowAPI.toggleAction(id)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const updateTrigger = createAsyncThunk(
  "workflow/updateTrigger",
  async ({ id, actionIds, actionsConfig }, { rejectWithValue }) => {
    try {
      return await workflowAPI.updateTriggerActions(id, actionIds, actionsConfig)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const createTrigger = createAsyncThunk(
  "workflow/createTrigger",
  async (data, { rejectWithValue }) => {
    try {
      return await workflowAPI.createTrigger(data)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const deleteTrigger = createAsyncThunk(
  "workflow/deleteTrigger",
  async (id, { rejectWithValue }) => {
    try {
      await workflowAPI.deleteTrigger(id)
      return id
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const duplicateTrigger = createAsyncThunk(
  "workflow/duplicateTrigger",
  async (id, { rejectWithValue }) => {
    try {
      return await workflowAPI.duplicateTrigger(id)
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadAvailableFields = createAsyncThunk(
  "workflow/loadAvailableFields",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchAvailableFields()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadSupervisors = createAsyncThunk(
  "workflow/loadSupervisors",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchUsersBySubRole('supervisor')
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadAssignableCoordinators = createAsyncThunk(
  "workflow/loadAssignableCoordinators",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchAssignableCoordinators()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

export const loadWebhookConfig = createAsyncThunk(
  "workflow/loadWebhookConfig",
  async (_, { rejectWithValue }) => {
    try {
      return await workflowAPI.fetchWebhookConfig()
    } catch (error) {
      return rejectWithValue(normalizeError(error.response?.data?.error || error))
    }
  }
)

const initialState = {
  conditions: [],
  triggers: [],
  actions: [],
  actionTypes: [],
  availableFields: [],
  supervisors: [],
  assignableUsers: [],
  webhookConfig: {
    webhookUrl: '',
    apiKey: ''
  },
  selectedTrigger: null,
  selectedAction: null,
  filters: {
    type: null,
    isActive: null,
    searchQuery: ""
  },
  pagination: {
    page: 1,
    rowsPerPage: 10,
    totalCount: 0
  },
  loading: false,
  error: null,
  stats: {
    totalTriggers: 0,
    totalActions: 0,
    activeActions: 0,
    disabledActions: 0
  }
}

const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setSelectedTrigger: (state, action) => {
      state.selectedTrigger = action.payload
    },
    setSelectedAction: (state, action) => {
      state.selectedAction = action.payload
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        type: null,
        isActive: null,
        searchQuery: ""
      }
    },
    clearError: (state) => {
      state.error = null
    },
    updateStats: (state) => {
      state.stats.totalTriggers = state.triggers.length
      state.stats.totalActions = state.actions.length
      state.stats.activeActions = state.actions.filter(a => a.isActive).length
      state.stats.disabledActions = state.actions.filter(a => !a.isActive).length
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadConditions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadConditions.fulfilled, (state, action) => {
        state.loading = false
        state.conditions = action.payload
      })
      .addCase(loadConditions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadTriggers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadTriggers.fulfilled, (state, action) => {
        state.loading = false
        state.triggers = action.payload
        state.stats.totalTriggers = action.payload.length
      })
      .addCase(loadTriggers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadActions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadActions.fulfilled, (state, action) => {
        state.loading = false
        state.actions = action.payload
        state.stats.totalActions = action.payload.length
        state.stats.activeActions = action.payload.filter(a => a.isActive).length
        state.stats.disabledActions = action.payload.filter(a => !a.isActive).length
      })
      .addCase(loadActions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadActionTypes.pending, (state) => {
        state.loading = true
      })
      .addCase(loadActionTypes.fulfilled, (state, action) => {
        state.loading = false
        state.actionTypes = action.payload
      })
      .addCase(loadActionTypes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(updateConfig.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.loading = false
        const index = state.actions.findIndex(a => a._id === action.payload._id)
        if (index !== -1) {
          state.actions[index] = action.payload
        }
      })
      .addCase(updateConfig.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(toggleActionStatus.pending, (state) => {
        state.loading = true
      })
      .addCase(toggleActionStatus.fulfilled, (state, action) => {
        state.loading = false
        const index = state.actions.findIndex(a => a._id === action.payload._id)
        if (index !== -1) {
          state.actions[index] = action.payload
        }
        state.stats.activeActions = state.actions.filter(a => a.isActive).length
        state.stats.disabledActions = state.actions.filter(a => !a.isActive).length
      })
      .addCase(toggleActionStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(updateTrigger.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateTrigger.fulfilled, (state, action) => {
        state.loading = false
        const index = state.triggers.findIndex(t => t._id === action.payload._id)
        if (index !== -1) {
          state.triggers[index] = action.payload
        }
      })
      .addCase(updateTrigger.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(createTrigger.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTrigger.fulfilled, (state, action) => {
        state.loading = false
        state.triggers.push(action.payload)
        state.stats.totalTriggers = state.triggers.length
      })
      .addCase(createTrigger.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(deleteTrigger.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTrigger.fulfilled, (state, action) => {
        state.loading = false
        state.triggers = state.triggers.filter(t => t._id !== action.payload)
        state.stats.totalTriggers = state.triggers.length
      })
      .addCase(deleteTrigger.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(duplicateTrigger.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(duplicateTrigger.fulfilled, (state, action) => {
        state.loading = false
        state.triggers.push(action.payload)
        state.stats.totalTriggers = state.triggers.length
      })
      .addCase(duplicateTrigger.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadAvailableFields.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadAvailableFields.fulfilled, (state, action) => {
        state.loading = false
        state.availableFields = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(loadAvailableFields.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadWebhookConfig.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadWebhookConfig.fulfilled, (state, action) => {
        state.loading = false
        state.webhookConfig = action.payload
      })
      .addCase(loadWebhookConfig.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadSupervisors.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadSupervisors.fulfilled, (state, action) => {
        state.loading = false
        state.supervisors = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(loadSupervisors.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    builder
      .addCase(loadAssignableCoordinators.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadAssignableCoordinators.fulfilled, (state, action) => {
        state.loading = false
        state.assignableUsers = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(loadAssignableCoordinators.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  setSelectedTrigger,
  setSelectedAction,
  setFilters,
  clearFilters,
  clearError,
  updateStats,
  setPagination
} = workflowSlice.actions

export const selectConditions = (state) => state.workflow.conditions
export const selectTriggers = (state) => state.workflow.triggers
export const selectActions = (state) => state.workflow.actions
export const selectActionTypes = (state) => state.workflow.actionTypes
export const selectAvailableFields = (state) => state.workflow.availableFields
export const selectSupervisors = (state) => state.workflow.supervisors
export const selectAssignableUsers = (state) => state.workflow.assignableUsers
export const selectWebhookConfig = (state) => state.workflow.webhookConfig
export const selectSelectedTrigger = (state) => state.workflow.selectedTrigger
export const selectSelectedAction = (state) => state.workflow.selectedAction
export const selectFilters = (state) => state.workflow.filters
export const selectPagination = (state) => state.workflow.pagination
export const selectLoading = (state) => state.workflow.loading
export const selectError = (state) => state.workflow.error
export const selectStats = (state) => state.workflow.stats

export const selectFilteredActions = (state) => {
  const { actions, filters } = state.workflow
  let filtered = [...actions]

  if (filters.type) {
    filtered = filtered.filter(a => a.type === filters.type)
  }

  if (filters.isActive !== null) {
    filtered = filtered.filter(a => a.isActive === filters.isActive)
  }

  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase()
    filtered = filtered.filter(a =>
      a.name.toLowerCase().includes(query) ||
      a.key.toLowerCase().includes(query)
    )
  }

  return filtered
}

export default workflowSlice.reducer
